import React from "react";

/**
 * Generic skeleton placeholder component
 * @param {object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {object} props.style - Inline styles
 * @param {string} props.height - Height of the skeleton (default: "h-24")
 * @param {string} props.width - Width of the skeleton (default: "w-full")
 * @param {boolean} props.rounded - Whether to apply rounded corners
 * @returns {JSX.Element} Skeleton placeholder
 */
const SkeletonPlaceholder = ({ 
  className = "", 
  style = {}, 
  height = "h-24", 
  width = "w-full", 
  rounded = true 
}) => {
  const baseClasses = `animate-pulse bg-gray-200 ${height} ${width}`;
  const roundedClass = rounded ? "rounded-md" : "";
  const combinedClasses = `${baseClasses} ${roundedClass} ${className}`.trim();

  return <div className={combinedClasses} style={style} />;
};

/**
 * Skeleton for component container
 */
export const ComponentSkeleton = ({ className = "", style = {} }) => {
  return (
    <div className={`space-y-4 ${className}`} style={style}>
      <SkeletonPlaceholder height="h-32" />
      <SkeletonPlaceholder height="h-16" width="w-3/4" />
      <SkeletonPlaceholder height="h-20" width="w-1/2" />
    </div>
  );
};

/**
 * Skeleton for text content
 */
export const TextSkeleton = ({ lines = 3, className = "", style = {} }) => {
  return (
    <div className={`space-y-2 ${className}`} style={style}>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonPlaceholder
          key={i}
          height="h-4"
          width={i === lines - 1 ? "w-2/3" : "w-full"}
        />
      ))}
    </div>
  );
};

/**
 * Skeleton for image content
 */
export const ImageSkeleton = ({ className = "", style = {} }) => {
  return (
    <SkeletonPlaceholder
      height="h-48"
      className={`bg-gray-300 ${className}`}
      style={style}
    />
  );
};

/**
 * Skeleton for title content
 */
export const TitleSkeleton = ({ className = "", style = {} }) => {
  return (
    <SkeletonPlaceholder
      height="h-8"
      width="w-1/2"
      className={className}
      style={style}
    />
  );
};

/**
 * Skeleton for button content
 */
export const ButtonSkeleton = ({ className = "", style = {} }) => {
  return (
    <SkeletonPlaceholder
      height="h-10"
      width="w-32"
      className={`rounded-full ${className}`}
      style={style}
      rounded={false} // We handle rounding manually
    />
  );
};

export default SkeletonPlaceholder;